import { useEffect, useState } from 'react'

import { Clock01Icon, FireIcon, UserGroupIcon } from '@hugeicons/react'
import clsx from 'clsx'
import { DateTime } from 'luxon'
import Countdown from 'react-countdown'

import { Badge } from '@/shared/ui/Badge'
import { Card } from '@/shared/ui/Card'

import {
  MISSION_SHOW_STATUS_BADGE,
  MISSION_SHOW_STATUS_LABELS,
  type MissionShowStatus
} from '../../constants/showStatus'
import { missionShowCardBodyClass, missionShowCardClass } from './showCard'

type MissionStatusCardProps = {
  showStatus: MissionShowStatus
  statusHint: string
  totalParticipants: number | null
  hasParticipantRestriction?: boolean
  capacityStatusLabel?: string
  isCapacityFull?: boolean
  startAt?: string | null
  endAt?: string | null
  /** `open` quando a missão está em vigor — habilita contagem de encerramento. */
  timeSlug?: string
}

const OPENS_SOON_THRESHOLD_HOURS = 24

const isWithinHoursThreshold = (isoDate: string, hours: number): boolean =>
  DateTime.fromISO(isoDate).diff(DateTime.local(), 'hours').hours <= hours

type MissionTimePanelProps = {
  tone: 'opens' | 'ends'
  targetAt: string
  live: boolean
  className?: string
}

const MissionTimePanel = ({
  tone,
  targetAt,
  live,
  className
}: MissionTimePanelProps) => {
  const isEnds = tone === 'ends'
  const label = isEnds ? 'Encerra em' : 'Abre em'
  const formattedDate = DateTime.fromISO(targetAt).toLocaleString(
    DateTime.DATETIME_SHORT
  )

  return (
    <div
      className={clsx(
        'flex items-start gap-3 rounded-xl border px-4 py-3 shadow-sm',
        isEnds
          ? 'border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50/90'
          : 'border-brand/15 bg-gradient-to-br from-[#f9f5ff] to-white',
        className
      )}
    >
      <span
        className={clsx(
          'flex h-11 w-11 shrink-0 items-center justify-center rounded-lg',
          isEnds ? 'bg-orange-100 text-orange-600' : 'bg-brand-light text-brand'
        )}
      >
        {isEnds ? (
          <FireIcon size={20} variant="duotone" aria-hidden />
        ) : (
          <Clock01Icon size={20} variant="duotone" aria-hidden />
        )}
      </span>

      <div className="min-w-0 flex-1">
        <span
          className={clsx(
            'text-caps font-bold uppercase tracking-wide',
            isEnds ? 'text-orange-700' : 'text-brand'
          )}
        >
          {label}
        </span>

        {live ? (
          <Countdown
            date={DateTime.fromISO(targetAt).toJSDate()}
            zeroPadTime={2}
            renderer={({ days, hours, minutes, seconds }) => (
              <p
                className={clsx(
                  'm-0 mt-1 font-mono text-lg font-bold leading-none tabular-nums tracking-tight',
                  isEnds ? 'text-orange-900' : 'text-gray-900'
                )}
              >
                {days > 0 && <span>{days}d </span>}
                {String(hours).padStart(2, '0')}:
                {String(minutes).padStart(2, '0')}:
                {String(seconds).padStart(2, '0')}
              </p>
            )}
          />
        ) : (
          <p className="m-0 mt-1 text-sm font-bold leading-snug text-gray-900">
            {formattedDate}
          </p>
        )}

        <p className="m-0 mt-1.5 text-2xs leading-snug text-gray-600">
          {isEnds
            ? 'Envie sua evidência antes do encerramento para garantir sua participação.'
            : 'A participação será liberada automaticamente nesta data.'}
        </p>
      </div>
    </div>
  )
}

const CapacityFullNotice = ({
  capacityStatusLabel
}: {
  capacityStatusLabel?: string
}) => (
  <p className="m-0 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-xs font-semibold leading-snug text-red-700">
    {capacityStatusLabel ?? 'Lotada'} — aguarde novas vagas para participar
    novamente.
  </p>
)

export const MissionStatusCard = ({
  showStatus,
  statusHint,
  totalParticipants,
  hasParticipantRestriction = false,
  capacityStatusLabel,
  isCapacityFull,
  startAt,
  endAt,
  timeSlug
}: MissionStatusCardProps) => {
  const showOpensCountdown = showStatus === 'not_started' && !!startAt
  const showEndsCountdown =
    timeSlug === 'open' &&
    !!endAt &&
    showStatus !== 'approved' &&
    showStatus !== 'expired'

  const [opensSoon, setOpensSoon] = useState(
    () => showOpensCountdown && isWithinHoursThreshold(startAt!, OPENS_SOON_THRESHOLD_HOURS)
  )

  useEffect(() => {
    if (!showOpensCountdown) return undefined

    const interval = setInterval(() => {
      const withinThreshold = isWithinHoursThreshold(startAt!, OPENS_SOON_THRESHOLD_HOURS)
      setOpensSoon(prev => (prev !== withinThreshold ? withinThreshold : prev))
    }, 1000)
    return () => clearInterval(interval)
  }, [showOpensCountdown, startAt])

  /* Missão em vigor ou ainda não aberta: o painel de tempo É o card —
     sem wrapper, sem badge "Disponível" (MASTER: `available` não é exceção). */
  if (showEndsCountdown) {
    return (
      <div className="mb-4 flex flex-col gap-2">
        <MissionTimePanel tone="ends" targetAt={endAt!} live />
        {hasParticipantRestriction && isCapacityFull && (
          <CapacityFullNotice capacityStatusLabel={capacityStatusLabel} />
        )}
      </div>
    )
  }

  if (showOpensCountdown) {
    return (
      <div className="mb-4 flex flex-col gap-2">
        <MissionTimePanel
          tone="opens"
          targetAt={startAt!}
          live={opensSoon}
        />
        {hasParticipantRestriction && isCapacityFull && (
          <CapacityFullNotice capacityStatusLabel={capacityStatusLabel} />
        )}
      </div>
    )
  }

  /* Estados excepcionais: badge só quando informa algo acionável (nunca `available`). */
  const showStatusBadge = showStatus !== 'available'

  return (
    <Card className={missionShowCardClass}>
      <Card.Body className={missionShowCardBodyClass}>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            {showStatusBadge && (
              <>
                <span className="text-caps font-bold uppercase tracking-wide text-gray-700">
                  Status da missão
                </span>
                <div className="mt-2">
                  <Badge
                    variant={MISSION_SHOW_STATUS_BADGE[showStatus]}
                    size="md"
                    showIcon={false}
                  >
                    {MISSION_SHOW_STATUS_LABELS[showStatus]}
                  </Badge>
                </div>
              </>
            )}
            <p
              className={clsx(
                'm-0 text-sm leading-snug text-gray-700',
                showStatusBadge && 'mt-2'
              )}
            >
              {statusHint}
            </p>
          </div>

          {totalParticipants !== null && (
            <div className="flex shrink-0 flex-col items-end rounded-xl border border-contour bg-gray-50 px-3 py-2 text-right">
              <span className="inline-flex items-center gap-1 text-2xs font-bold uppercase tracking-wide text-gray-500">
                <UserGroupIcon size={12} aria-hidden className="text-primary" />
                Participantes
              </span>
              <span className="mt-0.5 font-mono text-xl font-bold leading-none tabular-nums text-gray-900">
                {totalParticipants}
              </span>
            </div>
          )}
        </div>

        {hasParticipantRestriction && isCapacityFull && (
          <CapacityFullNotice capacityStatusLabel={capacityStatusLabel} />
        )}
      </Card.Body>
    </Card>
  )
}
